import { useQueries, useQuery } from 'react-query';
import * as PlatformAdminService from 'services/PlatformAdmin/PlatformAdmin';
import * as FlowBuilderService from 'services/FlowBuilder/FlowBuilder';
import * as LaunchpadService from 'services/Launchpad/Launchpad';
import * as LaucnhpadConstants from 'constants/launchpad';
import { Flow } from 'types/FlowBuilder';
import { Permissions } from 'types/Role';
import { AccountInfoCounters } from 'types/Launchpad';
import { validatePermission } from 'utils/launchpad';

export const useLaunchpadQuery = (
  userRoleIsSuperAdmin: boolean,
  macroName = '',
  corpAcctNbr: number,
  sso: string,
) => {
  const [macroQuery, accountInfoCounterQuery, isVoiceAccountQuery] = useQueries(
    [
      {
        queryKey: ['getMacroRole'],
        queryFn: async () => {
          if (!userRoleIsSuperAdmin) {
            const {
              data,
            }: {
              data: Permissions[];
            } = await PlatformAdminService.getMacroRoles(macroName);
            return data;
          }
          return [];
        },
      },
      {
        queryKey: ['getInfoCounter'],
        queryFn: async () => {
          const {
            data,
          }: {
            data: AccountInfoCounters;
          } = await LaunchpadService.getAccountInfoCountersV2(sso, corpAcctNbr);
          return data;
        },
      },
      {
        queryKey: ['getIsVoiceAccount'],
        queryFn: async () => {
          const {
            data,
          }: {
            data: boolean;
          } = await LaunchpadService.getIsVoiceAccount(sso, corpAcctNbr);
          return data;
        },
      },
    ],
  );

  const flowsQuery = useQuery(
    'getFlows',
    async () => {
      const { data }: { data: Flow[] } = await FlowBuilderService.getFlows(
        corpAcctNbr,
        sso,
      );
      return data;
    },
    {
      enabled:
        !macroQuery.isLoading &&
        validatePermission(
          LaucnhpadConstants.PERMISSION_TYPES.communications.resourceId,
          macroQuery.data,
          LaucnhpadConstants.PERMISSION_TYPES.communications.voice,
          userRoleIsSuperAdmin,
        ),
    },
  );

  const isQueryLoading =
    isVoiceAccountQuery.isLoading ||
    accountInfoCounterQuery.isLoading ||
    macroQuery.isLoading ||
    flowsQuery.isLoading;

  const isQueryError =
    macroQuery.isError ||
    flowsQuery.isError ||
    accountInfoCounterQuery.isError ||
    isVoiceAccountQuery.isError;

  const callFlowCount = flowsQuery.data?.length ?? 0;
  const userCount = accountInfoCounterQuery.data?.userCount ?? 0;
  const teamCount = accountInfoCounterQuery.data?.teamCount ?? 0;
  const macroQueryData = macroQuery.data;
  const isVoiceAccount = isVoiceAccountQuery.data ?? false;

  return {
    isQueryLoading,
    isQueryError,
    callFlowCount,
    macroQueryData,
    userCount,
    teamCount,
    isVoiceAccount,
  };
};
